'use client';
import React, { useState, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Input,
  Icon,
  IConversationState,
  Avatar,
  IConversation,
  Button,
  IConversationMemberOption,
} from '@ui/components';
import { useRouter } from 'next/navigation';
import dayjs from 'dayjs';
import { useDebounce } from 'use-debounce';
import { abbreviateNumber } from '@ui/lib/utils';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface ConversationRecipientsProps {
  conversation: IConversation;
  state: IConversationState;
}

const ConversationRecipients = ({ conversation, state }: ConversationRecipientsProps) => {
  const scrollContainerId = 'recipients-scroll-container';
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');

  const {
    fans,
    loadConversationFans,
    loadMoreFans,
    hasMoreFans,
    isFansLoading,
    isFansFetching,
    handleFansSearch,
    totalFans,
    searchCrewMembers,
    onAddConversationParticipants,
  } = state || {};
  const term = inputValue.trim();
  const canAddPeople = Boolean(searchCrewMembers && onAddConversationParticipants);

  // One box: it filters the people in this message and finds crew members to add.
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    handleFansSearch?.(e.target.value.trim());
  };

  const clearSearch = () => {
    setInputValue('');
    handleFansSearch?.('');
  };

  useEffect(() => {
    if (conversation?.id && loadConversationFans) {
      loadConversationFans(conversation?.id);
    }
  }, [conversation?.id, loadConversationFans]);

  // Get source information from conversation's miscellaneous data
  const getSourceInfo = () => {
    if (!conversation?.miscellaneous) return null;

    const source = conversation?.miscellaneous?.from;

    if (source === 'cohort') {
      return {
        type: 'Cohort',
        created_at: conversation?.miscellaneous?.created_at,
        name: conversation?.miscellaneous?.cohort_name,
        icon: 'people',
      };
    } else if (source === 'custom_leaderboard') {
      return {
        type: 'Leaderboard',
        created_at: conversation?.miscellaneous?.created_at,
        name: conversation?.miscellaneous?.custom_leaderboard_name,
        icon: 'trophy',
      };
    } else if (source === 'emails') {
      return {
        type: 'Emails',
        icon: 'message',
      };
    } else if (source === 'members') {
      return {
        type: 'Members',
        icon: 'people',
      };
    }

    return null;
  };

  const sourceInfo = getSourceInfo();

  return (
    <div className="flex h-full flex-col p-4">
      {/* Source information banner */}
      {sourceInfo && (
        <div className="mb-4 rounded-md bg-orange-50 p-3">
          <div className="flex items-start">
            <Icon
              type={sourceInfo?.icon as any}
              className="mr-2 h-5 w-5 text-orange-500"
              fill="#FFA500"
            />
            <div className="text-sm font-medium text-orange-700 flex flex-col justify-start">
              <div className="flex items-start ">
                Recipients from: {sourceInfo?.type}
                {sourceInfo.name && (
                  <span className="ml-1 text-orange-600 text-ellipsis max-w-[190px] overflow-hidden text-nowrap font-semibold">
                    {sourceInfo?.name}
                  </span>
                )}
              </div>
              {sourceInfo?.created_at && (
                <span className="text-orange-700 text-xs">
                  Created: {dayjs(sourceInfo?.created_at).format('DD/MM/YYYY')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        {/* The clear button sits in the input's own row, so the text stops before it. */}
        <Input
          value={inputValue}
          placeholder={
            canAddPeople ? 'Search or add people by name or email...' : 'Search by name or email...'
          }
          rawInputClassName="min-w-0 text-ellipsis"
          onChange={handleInputChange}
          preFixComponent={<Icon type="search" className="mr-2 h-4 w-4 shrink-0 text-gray-400" />}
          postFixComponent={
            inputValue.length > 0 && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearSearch}
                className="ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-300 transition-colors hover:bg-red-400">
                <X className="h-4 w-4 text-white" />
              </button>
            )
          }
        />
      </div>

      <div id={scrollContainerId} className="h-[350px] overflow-y-auto pr-2">
        {term && canAddPeople && <p className={SECTION_TITLE}>In this message</p>}
        {isFansLoading && !fans?.length ? (
          <div className={`flex items-center justify-center ${term ? 'h-16' : 'h-40'}`}>
            <Icon type="loadingSpinner" className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !fans || fans.length === 0 ? (
          <div
            className={`flex items-center justify-center text-gray-500 ${
              term ? 'h-12 text-sm' : 'h-40'
            }`}>
            {term ? `No one in this message matches "${term}"` : 'No recipients found'}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={fans.length || 0}
            next={loadMoreFans || (() => {})}
            hasMore={!!hasMoreFans}
            scrollableTarget={scrollContainerId}
            loader={
              <div className="flex justify-center py-4">
                <Icon type="loadingSpinner" className="h-6 w-6 animate-spin text-primary" />
              </div>
            }
            endMessage={
              term ? null : (
                <div className="text-center text-xs py-2 text-gray-300">All recipients loaded</div>
              )
            }>
            <div className="space-y-2">
              {fans.map((fan) => (
                <div
                  key={fan.id}
                  className="flex items-center rounded-md border border-gray-200 p-3 transition-colors hover:bg-gray-50 cursor-pointer"
                  onClick={() => router.push(`/fan-profile/?fanId=${fan?.fan_id}`)}>
                  <div className="mr-3 h-10 w-10 flex-shrink-0 overflow-hidden rounded-full">
                    <Avatar size="s" src={fan?.avatar_url} displayName={fan?.display_name} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-gray-900">
                      {fan?.display_name || fan?.user_name || 'Unknown User'}
                    </div>
                    <div className="truncate text-sm text-gray-500">{fan?.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </InfiniteScroll>
        )}

        {term && canAddPeople && (
          <CrewMembersToAdd
            term={term}
            conversationId={conversation.id}
            recipientIds={(fans || []).map((recipient) => recipient.fan_id)}
            search={searchCrewMembers}
            onAdd={onAddConversationParticipants}
          />
        )}
      </div>

      {!isFansLoading && fans && fans.length > 0 && (
        <div className="mt-2 text-center text-sm text-gray-500">
          Showing {fans?.length} of {abbreviateNumber(totalFans)}{' '}
          {totalFans === 1 ? 'recipient' : 'recipients'}
        </div>
      )}
    </div>
  );
};

const SECTION_TITLE = 'mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500';

interface CrewMembersToAddProps {
  term: string;
  conversationId: string;
  recipientIds: string[];
  search: IConversationState['searchCrewMembers'];
  onAdd: IConversationState['onAddConversationParticipants'];
}

/**
 * The crew members who match the box but are not in this message yet, each one click from being added.
 */
const CrewMembersToAdd = ({
  term,
  conversationId,
  recipientIds,
  search,
  onAdd,
}: CrewMembersToAddProps) => {
  const [debouncedTerm] = useDebounce(term, 300);
  const [matches, setMatches] = useState<IConversationMemberOption[]>([]);
  const [isSearching, setIsSearching] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<string[]>([]);
  // Only the newest search may fill the list; an older one can answer after it.
  const latestSearch = useRef(0);

  useEffect(() => {
    const searchId = ++latestSearch.current;
    setIsSearching(true);
    setHasFailed(false);
    search(debouncedTerm)
      .then((members) => {
        if (searchId === latestSearch.current) setMatches(members);
      })
      .catch(() => {
        if (searchId !== latestSearch.current) return;
        setMatches([]);
        setHasFailed(true);
      })
      .finally(() => {
        if (searchId === latestSearch.current) setIsSearching(false);
      });
  }, [debouncedTerm, search]);

  const add = async (member: IConversationMemberOption) => {
    setAddingId(member.fan_id);
    try {
      await onAdd(conversationId, [member.fan_id]);
      setAddedIds((ids) => [...ids, member.fan_id]);
      toast(`${member.display_name} was added to this message`);
    } catch {
      toast(`Couldn't add ${member.display_name}. Try again.`);
    } finally {
      setAddingId(null);
    }
  };

  const isStale = isSearching || debouncedTerm !== term;
  const toAdd = matches.filter(
    (member) => !recipientIds.includes(member.fan_id) && !addedIds.includes(member.fan_id),
  );

  return (
    <section aria-label="Add to this message" className="mt-4">
      <p className={SECTION_TITLE}>Add to this message</p>
      {isStale && !toAdd.length ? (
        <div className="flex h-12 items-center justify-center">
          <Icon type="loadingSpinner" className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : hasFailed ? (
        <p className="py-3 text-center text-sm text-gray-500">
          Couldn&apos;t search your crew. Try again.
        </p>
      ) : !toAdd.length ? (
        <p className="py-3 text-center text-sm text-gray-500">
          No one else in your crew matches &quot;{term}&quot;
        </p>
      ) : (
        <ul className="space-y-2">
          {toAdd.map((member) => (
            <li
              key={member.fan_id}
              className="flex items-center rounded-md border border-dashed border-gray-200 p-3">
              <div className="mr-3 h-10 w-10 flex-shrink-0 overflow-hidden rounded-full">
                <Avatar size="s" src={member.avatar_url} displayName={member.display_name} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-gray-900">{member.display_name}</div>
                {member.email && (
                  <div className="truncate text-sm text-gray-500">{member.email}</div>
                )}
              </div>
              <Button
                type="outline"
                size="small"
                className="shrink-0"
                aria-label={`Add ${member.display_name}`}
                isLoading={addingId === member.fan_id}
                disabled={addingId !== null}
                onClick={() => add(member)}>
                Add
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ConversationRecipients;
